import { navigation, React, IS_SSR, AppIcon, StyleSheets, StyleRegistry, Chat } from '@/config'
import { View, Button, CompanyLogo, Text, Badge, Icon } from '@/components'
import { StylesOf } from '@codeleap/types'
import { ICSS, StyledComponent, StyleProp, useCompositionStyles } from '@codeleap/styles'
import { NavigationMenuComposition } from '@/config/stylesheets/NavigationMenu'
import { BadgeComposition, useStylesFor } from '@codeleap/web'
import { Profiles } from '@/services'

export type NavigateLink = {
  text: string
  icon: AppIcon
  selectedIcon?: AppIcon
  route?: AppRoute
  feature?: 'accesshub' | 'chat'
}

export type NavigationMenuProps = {
  withDrawer?: boolean
  showLogo?: boolean

  styles?: StylesOf<NavigationMenuComposition>
  children?: React.ReactNode
}

const allLinks: NavigateLink[] = [
  { text: 'Home', icon: 'home-line', selectedIcon: 'home-line-fill', route: 'AccessHub.Home', feature: 'accesshub' },
  { text: 'Chats', icon: 'message-chat-circle', selectedIcon: 'message-chat-circle-fill', route: 'Chat.Chat', feature: 'chat' },
  { text: 'Settings', icon: 'settings', selectedIcon: 'settings-5-fill', route: 'Profile.Edit' },
]

const inRoute = (route: AppRoute) => {
  if (IS_SSR) return false

  let path = window?.location?.pathname

  if (!path?.startsWith('/')) {
    path = '/' + path
  }

  if (!path?.endsWith('/')) {
    path = path + '/'
  }

  const routePath = navigation.getPathWithParams(route as any, {})

  return path?.includes(routePath)
}

export const NavigationMenu: StyledComponent<typeof StyleSheets.NavigationMenuStyles, NavigationMenuProps> = (props) => {
  const {
    withDrawer = false,
    showLogo = false,

    style,
    children,
  } = props

  const styles = useStylesFor(NavigationMenu.styleRegistryName, style)
  const compositionStyles = useCompositionStyles(['linkButton', 'logo'], styles)
  const unreadCount = Chat.Store.useUnreadCount()

  const hasAccessHub = Profiles.Store.useHasFeature('accesshub')
  const hasChat = Profiles.Store.useHasFeature('chat')

  const links = React.useMemo(() => {
    return allLinks.filter(link => {
      if (!link.feature) return true
      if (link.feature === 'accesshub') return hasAccessHub
      if (link.feature === 'chat') return hasChat
      return true
    })
  }, [hasAccessHub, hasChat])

  const RenderLink = React.useCallback(({ link, isFirst, isLast, unreadCount }: { link: NavigateLink; isFirst: boolean; isLast: boolean; unreadCount: number }) => {
    const isSelected = inRoute(link.route)
    const isChat = link.route === 'Chat.Chat'
    const icon = isSelected ? (link?.selectedIcon || link.icon) : link.icon
    const _style = [
      styles.linkWrapper,
      isFirst && styles['linkWrapper:first'],
      isLast && styles['linkWrapper:last'],
    ]

    const linkButtonStyles = compositionStyles.linkButton
    const commonButtonProps = {
      debugName: `NavigationMenu-link-${link.text}`,
      selected: isSelected,
      onPress: () => navigation.navigate(link.route as any),
    }

    const renderBadge = (style: StyleProp<Record<BadgeComposition, ICSS>>) => (isChat && unreadCount > 0) && (
      <Badge
        style={style}
        badge={unreadCount}
        renderBadgeContent={({ count }) => (
          <Text
            style={[
              'semiBold',
              'c2',
              'color:neutralSolid0',
              'paddingVertical:0.5',
              `paddingHorizontal:1`,
            ]}
            text={String(count)}
          />
        )}
      />
    )

    return (
      <View style={_style}>
        {withDrawer ? (
          <Button
            {...commonButtonProps}
            style={linkButtonStyles}
            text={link.text}
            icon={icon}
          >
            {renderBadge(['relative', 'alignSelfCenter', 'number'])}
          </Button>
        ) : isChat ? (
          <Button {...commonButtonProps} style={['relative', linkButtonStyles]}>
            <View style={['fullWidth', 'fullHeight', 'row', 'alignCenter', 'justifyCenter', 'gap:1']}>
              <View style={['column']}>
                <Icon
                  name={icon}
                  // @ts-ignore
                  style={[linkButtonStyles.icon, isSelected && linkButtonStyles['icon:selected']]}
                />
                <Text
                  // @ts-ignore
                  style={[linkButtonStyles.text, isSelected && linkButtonStyles['text:selected']]}
                >
                  {link.text}
                </Text>
              </View>
              {renderBadge([(linkButtonStyles as any).badge, 'numberSmall', isSelected ? 'numberSmall:selected' : null] as any)}
            </View>
          </Button>
        ) : (
          <Button
            {...commonButtonProps}
            style={compositionStyles.linkButton}
            text={link.text}
            icon={icon}
          />
        )
        }
      </View >
    )
  }, [])

  return (
    <View style={[styles.wrapper, withDrawer ? styles['content:drawer'] : styles.content]}>
      {showLogo && (
        <CompanyLogo

          style={compositionStyles.logo}

        />
      )}

      {links ? (
        <View style={[styles.linksWrapper]}>
          {links?.map((link, idx) => (
            <RenderLink
              link={link}
              isFirst={idx === 0}
              isLast={idx === links.length - 1}
              unreadCount={unreadCount}
              key={'header-' + link?.text}
            />
          ))}
        </View>
      ) : null}

      {children}
    </View>
  )
}

NavigationMenu.styleRegistryName = 'NavigationMenu'
NavigationMenu.elements = ['wrapper', 'content', 'content:drawer', 'linksWrapper', 'linkWrapper:first', 'linkWrapper:last', 'linkButton']

StyleRegistry.registerComponent(NavigationMenu)
